import { FC, useMemo } from 'react';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { StaticDatePicker, PickersDay } from '@mui/x-date-pickers';
import { format, isBefore, isAfter } from 'date-fns';
import clsx from 'clsx';
import { ArrowRightIcon, ArrowLeftIcon } from '@/modules/_core/icons';
import { eventDatePickerStyles } from './event-date-picker.styles';
import { Showable, useStyles } from '@sweatpals/react-ui-kit';
import { getDarken, getLighten } from '@/modules/_core/utils/color';
import { defaultHostColorHex } from '../../pages/host/host-page.styles';

export interface EventDatePickerProps {
	value?: Date | null;
	isLoading: boolean;
	primaryColor?: string;
	sx?: SxProps<Theme>;
	groupedEventsDates?: { [key: string]: Date[] };
	onChange?(date: Date): void;
	onMonthChange?(date: Date): void;
	onYearChange?(date: Date): void;
	dayOfWeekFormatter?(day: string, date: Date): string;
}

export const EventDatePicker: FC<EventDatePickerProps> = (props) => {
	const {
		sx,
		isLoading,
		primaryColor = defaultHostColorHex,
		groupedEventsDates,
		...datePickerProps
	} = props;

	const backgroundColor = useMemo(() => getDarken(primaryColor, 1), [primaryColor]);
	const lightTextColor = useMemo(() => getLighten(primaryColor, 1), [primaryColor]);
	const styles = useStyles(eventDatePickerStyles, primaryColor, backgroundColor, lightTextColor);

	return (
		<StaticDatePicker
			{...datePickerProps}
			showDaysOutsideCurrentMonth
			fixedWeekNumber={6}
			sx={styles.datePicker}
			slots={{
				toolbar: () => null,
				actionBar: () => null,
				day: (props) => {
					const {
						day,
						today,
						selected,
						className,
						...pickersDayProps
					} = props;
					const dayId = format(day, 'yyyy-MM-dd');
					const eventsCount = groupedEventsDates?.[dayId]?.length ?? 0;
					const isPast = isBefore(day, new Date()) && !today;
					const isFuture = isAfter(day, new Date()) && !today;

					return (
						<PickersDay
							day={day}
							today={today}
							selected={selected}
							className={clsx(className, isPast && 'MuiPickersDay-past', isFuture && 'MuiPickersDay-future')}
							{...pickersDayProps}
						>
							<Typography variant="inherit">{day.getDate()}</Typography>
							<Box sx={{ display: 'flex', gap: '2px', minHeight: '4px' }}>
								{new Array(Math.min(eventsCount, 3)).fill(null)
									.map((_, index) => (
										<Box
											key={`event-mark-${index}`}
											sx={{
												width: '4px',
												height: '4px',
												borderRadius: '50%',
												backgroundColor: isPast ? '#515151' : selected ? '#FFFFFF' : primaryColor
											}}
										/>
									))
								}
							</Box>
						</PickersDay>
					);
				},
				calendarHeader: (props) => {
					const {
						view,
						disablePast,
						currentMonth: selectedMonth,
						onMonthChange,
						onViewChange
					} = props;
					const selectedMonthId = format(selectedMonth, 'yyyy-MM');
					const currentMonthId = format(new Date(), 'yyyy-MM');

					return (
						<Box sx={styles.datePickerHeader}>
							<IconButton
								sx={styles.datePickerIconButton}
								disabled={disablePast && selectedMonthId === currentMonthId}
								onClick={() => {
									const prevMonth = new Date(selectedMonth);
									prevMonth.setMonth(prevMonth.getMonth() - 1);
									onMonthChange(prevMonth, 'right');
								}}
							>
								<ArrowLeftIcon />
							</IconButton>
							<Typography sx={styles.datePickerTitle}>
								{format(selectedMonth, 'MMMM')}
								{' '}
								<Box
									component="span"
									sx={{ color: primaryColor, cursor: 'pointer' }}
									onClick={() => {
										onViewChange(view === 'day' ? 'year' : 'day');
									}}
								>
									{format(selectedMonth, 'yyyy')}
								</Box>
							</Typography>
							<IconButton
								sx={styles.datePickerIconButton}
								onClick={() => {
									const nextMonth = new Date(selectedMonth);
									nextMonth.setMonth(nextMonth.getMonth() + 1);
									onMonthChange(nextMonth, 'left');
								}}
							>
								<ArrowRightIcon />
							</IconButton>
							<Showable show={isLoading}>
								<Box sx={styles.louder} />
							</Showable>
						</Box>
					);
				},
			}}
		/>
	);
};
